import styled from 'styled-components'

import { theme } from '../../../constants'

export const PlaylistPanel = styled.div`
  min-width: 0;
  min-height: 100%;
  height: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xlarge};
  background: ${theme.colors.surface};
  padding: clamp(${theme.spacing[4]}, 3vw, ${theme.spacing[6]});
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.desktop}) and (orientation: portrait) {
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  @media (max-width: 720px) {
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  @media (max-height: 520px) and (orientation: landscape) {
    height: auto;
    min-height: 0;
    overflow: visible;
  }
`

export const PlaylistHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};

  > div > span {
    color: ${theme.roleColors.student.strong};
    font-size: ${theme.typography.size.caption};
    font-weight: ${theme.typography.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  h3 {
    margin: ${theme.spacing[1]} 0 0;
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.size.h3};
  }

  > small {
    flex: 0 0 auto;
    color: ${theme.colors.textSecondary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    align-items: start;
    flex-direction: column;
  }
`

export const CategoryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[6]};
`

export const CategoryButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active ? theme.roleColors.student.main : theme.colors.border};
  border-radius: ${theme.radii.pill};
  background: ${({ $active }) =>
    $active ? theme.roleColors.student.soft : theme.colors.surfaceElevated};
  color: ${({ $active }) =>
    $active ? theme.roleColors.student.text : theme.colors.textPrimary};
  min-height: ${theme.layout.controlHeight};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  display: flex;
  align-items: center;
  text-align: left;
  cursor: pointer;
  font: inherit;
  font-size: ${theme.typography.size.bodySmall};
  transition:
    transform ${theme.motion.fast} ${theme.motion.easing},
    background ${theme.motion.fast} ${theme.motion.easing};

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid ${theme.semanticColors.focus};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const TrackList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  align-content: start;
  gap: ${theme.spacing[3]};
  overflow-y: auto;
  padding: ${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[4]};
  scrollbar-width: thin;
  scrollbar-color: ${theme.roleColors.student.main} transparent;

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${theme.radii.pill};
    background: ${theme.roleColors.student.main};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  @media (max-width: ${theme.breakpoints.desktop}) and (orientation: portrait) {
    flex: none;
    max-height: 560px;
  }

  @media (max-width: 720px) {
    flex: none;
    max-height: 560px;
  }

  @media (max-height: 520px) and (orientation: landscape) {
    flex: none;
    max-height: 360px;
  }
`

export const TrackButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active ? theme.roleColors.student.main : theme.colors.border};
  border-radius: ${theme.radii.large};
  background: ${({ $active }) =>
    $active ? theme.roleColors.student.soft : theme.colors.surfaceElevated};
  color: ${theme.colors.textPrimary};
  min-height: 76px;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  display: grid;
  grid-template-columns: 28px 56px minmax(0, 1fr) 44px;
  align-items: center;
  gap: ${theme.spacing[3]};
  cursor: pointer;
  text-align: left;
  font: inherit;
  box-shadow: ${({ $active }) =>
    $active ? theme.shadows.small : theme.shadows.none};
  transition:
    transform ${theme.motion.normal} ${theme.motion.easing},
    box-shadow ${theme.motion.normal} ${theme.motion.easing},
    border-color ${theme.motion.normal} ${theme.motion.easing};

  > strong {
    color: ${({ $active }) =>
      $active
        ? `color-mix(in srgb, ${theme.roleColors.student.text} 68%, transparent)`
        : theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
  }

  .track-action {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    justify-self: end;
    border-radius: ${theme.radii.round};
    border: 1px solid ${theme.roleColors.student.main};
    color: ${theme.roleColors.student.text};
    background: ${theme.roleColors.student.soft};

    > svg {
      width: 15px;
      height: 15px;
      display: block;
      color: currentColor;
      fill: currentColor;
      stroke: currentColor;
    }
  }

  ${({ $active }) =>
    $active &&
    `
      .track-details > span {
        color: ${theme.roleColors.student.text};
      }

      .track-details > small {
        color: color-mix(
          in srgb,
          ${theme.roleColors.student.text} 68%,
          transparent
        );
      }

      .track-action {
        background: ${theme.roleColors.student.main};
        border-color: ${theme.roleColors.student.strong};
      }
    `}

  &:hover {
    position: relative;
    z-index: 2;
    transform: scale(1.02);
    transform-origin: center;
    border-color: ${theme.roleColors.student.main};
    box-shadow: ${theme.shadows.medium};
  }

  &:focus-visible {
    outline: 3px solid ${theme.semanticColors.focus};
    outline-offset: 2px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 48px minmax(0, 1fr) 44px;

    > strong {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`

export const TrackCover = styled.img`
  width: 56px;
  height: 56px;
  display: block;
  object-fit: cover;
  border-radius: ${theme.radii.round};
  border: 1px solid ${theme.colors.border};

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 48px;
    height: 48px;
  }
`

export const TrackDetails = styled.span`
  min-width: 0;
  display: grid;
  gap: ${theme.spacing[1]};

  > span {
    overflow: hidden;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.weight.semibold};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > small {
    overflow: hidden;
    color: ${theme.colors.textSecondary};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
